import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Archive, ArchiveRestore, Info, Lock, Megaphone, Pencil, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/core/hooks/useAuth";
import { Button } from "@/shared/components/Button";
import { Modal } from "@/shared/components/Modal";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import { Paginacao } from "@/shared/components/Paginacao";
import { CardComunicado } from "@/modules/comunicados/components/CardComunicado";
import { ModalLeituraComunicado } from "@/modules/comunicados/components/ModalLeituraComunicado";
import {
  useApagarComunicado,
  useArquivarComunicado,
  useComunicados,
  useMeusMinisteriosParticipo,
  usePodeGerenciarComunicados,
  usePrazosComunicados,
  useRestaurarComunicado,
} from "@/modules/comunicados/hooks/useComunicados";
import {
  COMUNICADOS_POR_PAGINA,
  type ComunicadoComRelacoes,
} from "@/modules/comunicados/services/comunicadosService";
import { dataCurtaNoFuso, diasParaApagar } from "@/modules/comunicados/utils/prazo";

const CLASSE_BOTAO_ACAO =
  "inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-border bg-transparent px-3 text-[13px] font-semibold text-text transition-colors hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50";

type Aba = "publicado" | "arquivado";

/**
 * Lista de comunicados. Mesma tela para todos, com o que o BANCO libera:
 * - Membro: os comunicados da igreja toda ou dos seus ministérios, do mais
 *   recente ao mais antigo — sem autor, destino ou prazo.
 * - Líder/gestor do elo: tudo isso + abas Publicados/Arquivados, destino,
 *   prazo, autor e as ações (editar, arquivar, restaurar, apagar).
 * - Administrador sem papel no elo: vê os detalhes, mas só lê (sem ações).
 * Esconder botão é só conveniência; cada ação é revalidada pela RPC.
 */
export function ListaComunicadosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pessoa, ehAdminPlataforma } = useAuth();

  const { data: podeGerenciar = false, isLoading: carregandoPermissao } = usePodeGerenciarComunicados();
  const verDetalhes = podeGerenciar || ehAdminPlataforma;

  const aba: Aba = verDetalhes && searchParams.get("aba") === "arquivados" ? "arquivado" : "publicado";
  const paginaPedida = Number.parseInt(searchParams.get("pagina") ?? "1", 10);
  const pagina = Number.isFinite(paginaPedida) && paginaPedida > 0 ? paginaPedida : 1;

  const { data, isLoading, isError, isFetching, refetch } = useComunicados(
    { situacao: aba, pagina },
    !carregandoPermissao,
  );
  const { data: meus } = useMeusMinisteriosParticipo(pessoa?.id, verDetalhes);
  const meusSet = meus ? new Set(meus) : undefined;
  const { data: prazos } = usePrazosComunicados(podeGerenciar && aba === "publicado");

  const arquivar = useArquivarComunicado();
  const restaurar = useRestaurarComunicado();
  const apagar = useApagarComunicado();

  const [lendo, setLendo] = useState<ComunicadoComRelacoes | null>(null);
  const [paraArquivar, setParaArquivar] = useState<ComunicadoComRelacoes | null>(null);
  const [paraApagar, setParaApagar] = useState<ComunicadoComRelacoes | null>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(
    (location.state as { aviso?: string } | null)?.aviso ?? null,
  );
  const [restaurado, setRestaurado] = useState<string | null>(null);

  // O aviso vindo de "Publicar"/"Salvar" vive só nesta visita (não reaparece ao recarregar).
  useEffect(() => {
    if ((location.state as { aviso?: string } | null)?.aviso) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  const itens = data?.itens ?? [];
  const total = data?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(total / COMUNICADOS_POR_PAGINA));

  // Apagou/arquivou o último item de uma página que deixou de existir: volta uma página.
  useEffect(() => {
    if (data && !isFetching && data.itens.length === 0 && pagina > 1) {
      irParaPagina(pagina - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isFetching, pagina]);

  function irParaPagina(nova: number) {
    const proximos = new URLSearchParams(searchParams);
    if (nova <= 1) proximos.delete("pagina");
    else proximos.set("pagina", String(nova));
    setSearchParams(proximos, { replace: true });
  }

  function trocarAba(nova: Aba) {
    const proximos = new URLSearchParams();
    if (nova === "arquivado") proximos.set("aba", "arquivados");
    setSearchParams(proximos, { replace: true });
  }

  const apagamAmanha = (prazos ?? []).filter((p) => diasParaApagar(p.contagem_inicio_em) === 1);

  async function confirmarArquivar() {
    if (!paraArquivar) return;
    setErroAcao(null);
    try {
      await arquivar.mutateAsync({ id: paraArquivar.id, versao: paraArquivar.versao });
      setParaArquivar(null);
      setAviso("Comunicado arquivado.");
    } catch (e) {
      setErroAcao(e instanceof Error ? e.message : "Não foi possível arquivar o comunicado.");
    }
  }

  async function confirmarApagar() {
    if (!paraApagar) return;
    setErroAcao(null);
    try {
      await apagar.mutateAsync({ id: paraApagar.id, versao: paraApagar.versao });
      setParaApagar(null);
      setAviso("Comunicado apagado.");
    } catch (e) {
      setErroAcao(e instanceof Error ? e.message : "Não foi possível apagar o comunicado.");
    }
  }

  async function aoRestaurar(c: ComunicadoComRelacoes) {
    setErroAcao(null);
    setAviso(null);
    try {
      const resultado = await restaurar.mutateAsync({ id: c.id, versao: c.versao });
      if (resultado.contagemReiniciada && resultado.apagaEm) {
        setRestaurado(
          `A contagem de 30 dias recomeçou. Ele será apagado em ${dataCurtaNoFuso(new Date(resultado.apagaEm))}.`,
        );
      } else {
        setAviso("Comunicado restaurado.");
      }
    } catch (e) {
      setErroAcao(e instanceof Error ? e.message : "Não foi possível restaurar o comunicado.");
    }
  }

  function fecharConfirmacao() {
    setParaArquivar(null);
    setParaApagar(null);
    setErroAcao(null);
  }

  const ocupado = arquivar.isPending || restaurar.isPending || apagar.isPending;

  return (
    <main className="mx-auto w-full max-w-2xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-black tracking-tight text-primary sm:text-3xl">Comunicados</h1>
          <p className="mt-1 text-sm text-muted">
            {podeGerenciar
              ? "Você gerencia este elo."
              : verDetalhes
                ? "Visão de administrador: somente leitura."
                : "Comunicados da sua igreja, do mais recente ao mais antigo."}
          </p>
        </div>
        {podeGerenciar && (
          <Button type="button" onClick={() => navigate("/comunicados/novo")} className="inline-flex shrink-0 items-center gap-1.5">
            <Plus size={16} aria-hidden="true" /> Novo
          </Button>
        )}
      </header>

      {verDetalhes && (
        <p className="mb-3 flex items-start gap-1.5 text-xs text-muted">
          <Lock size={14} className="mt-px shrink-0" aria-hidden="true" />
          Quem publicou aparece só para líderes e gestores do elo.
        </p>
      )}

      {aviso && (
        <p role="status" className="mb-3 rounded-md bg-success/10 px-4 py-3 text-sm text-success">
          {aviso}
        </p>
      )}
      {erroAcao && !paraArquivar && !paraApagar && (
        <p role="alert" className="mb-3 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
          {erroAcao}
        </p>
      )}

      {verDetalhes && (
        <div role="tablist" aria-label="Situação" className="mb-3.5 flex gap-1 rounded-xl bg-surface-2 p-1">
          {(
            [
              { chave: "publicado", rotulo: "Publicados" },
              { chave: "arquivado", rotulo: "Arquivados" },
            ] as const
          ).map((t) => (
            <button
              key={t.chave}
              type="button"
              role="tab"
              aria-selected={aba === t.chave}
              onClick={() => trocarAba(t.chave)}
              className={`min-h-9 flex-1 rounded-lg text-[13px] font-semibold transition-colors ${
                aba === t.chave ? "bg-surface text-text shadow-sm" : "text-muted hover:text-text"
              }`}
            >
              {t.rotulo}
              {aba === t.chave && data ? ` (${total})` : ""}
            </button>
          ))}
        </div>
      )}

      {podeGerenciar && aba === "publicado" && apagamAmanha.length > 0 && (
        <p className="mb-3 flex items-start gap-2 rounded-xl bg-warning/15 px-3 py-2.5 text-[13px] text-text">
          <Info size={16} className="mt-0.5 shrink-0 text-warning" aria-hidden="true" />
          <span>
            {apagamAmanha.length === 1
              ? "1 comunicado será apagado amanhã: "
              : `${apagamAmanha.length} comunicados serão apagados amanhã: `}
            {apagamAmanha.map((p) => p.titulo).join(", ")}. Para guardar, edite e escolha &quot;Manter até
            alguém apagar&quot;.
          </span>
        </p>
      )}

      {carregandoPermissao || isLoading ? (
        <div className="flex flex-col gap-2.5" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-2xl bg-surface-2" />
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-2xl border border-border bg-surface p-6 text-center text-sm text-muted">
          <p className="font-semibold text-text">Não foi possível carregar</p>
          <p>Verifique a conexão e tente de novo.</p>
          <Button type="button" onClick={() => void refetch()} className="mt-3">
            Tentar de novo
          </Button>
        </div>
      ) : itens.length === 0 ? (
        <div className="flex flex-col items-center gap-1.5 rounded-2xl border border-border bg-surface px-6 py-10 text-center">
          <Megaphone size={28} className="text-muted" aria-hidden="true" />
          <p className="text-sm font-semibold text-text">
            {verDetalhes
              ? aba === "publicado"
                ? "Nada publicado"
                : "Nada arquivado"
              : "Nenhum comunicado por enquanto"}
          </p>
          <p className="text-sm text-muted">
            {verDetalhes
              ? aba === "publicado"
                ? podeGerenciar
                  ? "Toque em Novo para publicar."
                  : "Quando a liderança publicar, aparece aqui."
                : "Comunicados arquivados aparecem aqui."
              : "Quando a liderança publicar, aparece aqui."}
          </p>
        </div>
      ) : (
        <>
          <ul className="flex flex-col gap-2.5">
            {itens.map((c) => (
              <CardComunicado
                key={c.id}
                comunicado={c}
                onAbrir={setLendo}
                acoes={
                  podeGerenciar ? (
                    <>
                      {c.situacao === "publicado" ? (
                        <>
                          <button
                            type="button"
                            disabled={ocupado}
                            onClick={() => navigate(`/comunicados/${c.id}/editar`)}
                            className={CLASSE_BOTAO_ACAO}
                          >
                            <Pencil size={14} aria-hidden="true" /> Editar
                          </button>
                          <button
                            type="button"
                            disabled={ocupado}
                            onClick={() => {
                              setErroAcao(null);
                              setParaArquivar(c);
                            }}
                            className={CLASSE_BOTAO_ACAO}
                          >
                            <Archive size={14} aria-hidden="true" /> Arquivar
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          disabled={ocupado}
                          onClick={() => void aoRestaurar(c)}
                          className={CLASSE_BOTAO_ACAO}
                        >
                          <ArchiveRestore size={14} aria-hidden="true" /> Restaurar
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={ocupado}
                        onClick={() => {
                          setErroAcao(null);
                          setParaApagar(c);
                        }}
                        className={`${CLASSE_BOTAO_ACAO} text-danger`}
                      >
                        <Trash2 size={14} aria-hidden="true" /> Apagar
                      </button>
                    </>
                  ) : undefined
                }
              />
            ))}
          </ul>
          <Paginacao paginaAtual={Math.min(pagina, totalPaginas)} totalPaginas={totalPaginas} onMudarPagina={irParaPagina} />
        </>
      )}

      <ModalLeituraComunicado
        comunicado={lendo}
        meusMinisterios={meusSet}
        podeEditar={podeGerenciar}
        onEditar={(c) => navigate(`/comunicados/${c.id}/editar`)}
        onFechar={() => setLendo(null)}
      />

      <ModalConfirmacao
        aberto={!!paraArquivar}
        variante="padrao"
        titulo="Arquivar comunicado?"
        mensagem="Ele deixa de aparecer para os membros. Arquivado, ele só será apagado se alguém apagar, mesmo que estivesse marcado para apagar em 30 dias. Você pode restaurar depois."
        textoBotaoConfirmar="Arquivar"
        carregando={arquivar.isPending}
        erro={erroAcao}
        onConfirmar={() => void confirmarArquivar()}
        onCancelar={fecharConfirmacao}
      />

      <ModalConfirmacao
        aberto={!!paraApagar}
        titulo="Apagar comunicado?"
        mensagem="Ele será apagado para todos e não poderá ser recuperado por aqui."
        textoBotaoConfirmar="Apagar"
        carregando={apagar.isPending}
        erro={erroAcao}
        onConfirmar={() => void confirmarApagar()}
        onCancelar={fecharConfirmacao}
      />

      <Modal titulo="Comunicado restaurado" aberto={!!restaurado} onClose={() => setRestaurado(null)}>
        <p className="text-sm text-muted">{restaurado}</p>
        <Button type="button" onClick={() => setRestaurado(null)} className="mt-4 w-full">
          Entendi
        </Button>
      </Modal>
    </main>
  );
}
